import React, { useState } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';

const ProductImageCarousel = ({ mainImage, mainImageDescription, images = [] }) => {
  // Combine main image with additional images, supporting both string URLs and {url, description} objects
  const processedImages = [
    { url: mainImage, description: mainImageDescription || '' },
    ...(images || []).map(img => 
      typeof img === 'string' 
        ? { url: img, description: '' }
        : { url: img.url, description: img.description || '' }
    )
  ].filter(img => img.url);
  
  const [currentIndex, setCurrentIndex] = useState(0);

  if (processedImages.length === 0) {
    return (
      <div className="aspect-square bg-gray-100 rounded-2xl flex items-center justify-center">
        <span className="text-gray-400">No image available</span>
      </div>
    );
  }

  const currentImage = processedImages[currentIndex];

  const goToPrevious = () => {
    setCurrentIndex((prev) => (prev === 0 ? processedImages.length - 1 : prev - 1));
  };

  const goToNext = () => {
    setCurrentIndex((prev) => (prev === processedImages.length - 1 ? 0 : prev + 1));
  };

  const goToSlide = (index) => {
    setCurrentIndex(index);
  };

  return (
    <div className="space-y-4" data-testid="product-image-carousel">
      {/* Main Image Display */}
      <div className="relative aspect-square bg-gray-50 rounded-2xl overflow-hidden group">
        <img
          src={currentImage.url}
          alt={currentImage.description || `Product image ${currentIndex + 1}`}
          className="w-full h-full object-contain"
          data-testid="carousel-main-image"
        />
        
        {/* Navigation Arrows (only show if multiple images) */}
        {processedImages.length > 1 && (
          <>
            <button
              onClick={goToPrevious}
              className="absolute left-4 top-1/2 -translate-y-1/2 w-10 h-10 bg-white/80 backdrop-blur-sm rounded-full flex items-center justify-center shadow-lg opacity-0 group-hover:opacity-100 transition-opacity hover:bg-white"
              data-testid="carousel-prev-btn"
            >
              <ChevronLeft className="h-6 w-6 text-gray-700" />
            </button>
            <button
              onClick={goToNext}
              className="absolute right-4 top-1/2 -translate-y-1/2 w-10 h-10 bg-white/80 backdrop-blur-sm rounded-full flex items-center justify-center shadow-lg opacity-0 group-hover:opacity-100 transition-opacity hover:bg-white"
              data-testid="carousel-next-btn"
            >
              <ChevronRight className="h-6 w-6 text-gray-700" />
            </button>
          </>
        )}

        {/* Image Counter */}
        {processedImages.length > 1 && (
          <div className="absolute bottom-4 right-4 bg-black/50 text-white px-3 py-1 rounded text-sm">
            {currentIndex + 1} / {processedImages.length}
          </div>
        )}
      </div>
      
      {/* Image Description */}
      {currentImage.description && (
        <div className="text-center px-4" data-testid="image-description-display">
          <p className="text-sm text-gray-600 italic">{currentImage.description}</p>
        </div>
      )}

      {/* Thumbnail Strip (only show if multiple images) */}
      {processedImages.length > 1 && (
        <div className="flex gap-2 overflow-x-auto pb-2">
          {processedImages.map((img, idx) => (
            <button
              key={idx}
              onClick={() => goToSlide(idx)}
              className={`flex-shrink-0 w-20 h-20 rounded-lg overflow-hidden border-2 transition-all ${
                idx === currentIndex
                  ? 'border-[#003456] ring-2 ring-[#003456]/30'
                  : 'border-transparent hover:border-gray-300'
              }`}
              data-testid={`carousel-thumbnail-${idx}`}
              title={img.description || `Image ${idx + 1}`}
            >
              <img
                src={img.url}
                alt={img.description || `Thumbnail ${idx + 1}`}
                className="w-full h-full object-cover"
              />
            </button>
          ))}
        </div>
      )}

      {/* Dots Indicator (alternative navigation for mobile) */}
      {processedImages.length > 1 && (
        <div className="flex justify-center gap-2 md:hidden">
          {processedImages.map((_, idx) => (
            <button
              key={idx}
              onClick={() => goToSlide(idx)}
              className={`w-2 h-2 rounded-full transition-all ${
                idx === currentIndex
                  ? 'bg-[#003456] w-6'
                  : 'bg-gray-300'
              }`}
            />
          ))}
        </div>
      )}
    </div>
  );
};

export default ProductImageCarousel;
